import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { DollarSign, Wallet, TrendingUp, Users, Loader2, ArrowDownToLine, Landmark, ExternalLink, AlertCircle } from "lucide-react";

const F = (n, d = 2) => (n == null ? "—" : Number(n).toFixed(d));

// Compact SVG line chart — no charting library needed for a series this small.
function SparkArea({ series, height = 90 }) {
  if (!series?.length) return null;
  const w = 640;
  const vals = series.map((d) => d.fees);
  const maxV = Math.max(1, ...vals);
  const stepX = series.length > 1 ? (w - 20) / (series.length - 1) : 0;
  const pts = series.map((d, i) => [10 + i * stepX, height - 10 - (d.fees / maxV) * (height - 20)]);
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `${path} L${pts.at(-1)[0]},${height - 10} L${pts[0][0]},${height - 10} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full" data-testid="earnings-spark">
      <path d={area} fill="hsl(var(--gain) / 0.15)" />
      <path d={path} fill="none" stroke="hsl(var(--gain))" strokeWidth="2" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2.5" fill="hsl(var(--gain))" />
      ))}
    </svg>
  );
}

export default function Earnings() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [withdrawing, setWithdrawing] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [payingOut, setPayingOut] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/earnings/summary?days=${days}`).then((r) => setSummary(r.data))
      .finally(() => setLoading(false));
  };
  const loadStripe = () => {
    api.get("/earnings/stripe/status").then((r) => setStripeStatus(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  useEffect(() => { loadStripe(); }, []);

  const withdraw = async () => {
    if (!summary?.unwithdrawn_balance) return;
    if (!window.confirm(`Withdraw $${F(summary.unwithdrawn_balance)} into your cash balance?`)) return;
    setWithdrawing(true);
    try {
      const { data } = await api.post("/earnings/withdraw");
      if (data.withdrawn > 0) {
        toast.success(`+$${F(data.withdrawn)} credited to your balance`);
      } else {
        toast.info(data.message || "Nothing to withdraw");
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Withdraw failed"); }
    finally { setWithdrawing(false); }
  };

  const connectStripe = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/earnings/stripe/connect", { origin_url: window.location.origin });
      if (data.connect_disabled) {
        toast.info("Stripe Connect isn't enabled yet — see the hint below.", { duration: 6000 });
        setStripeStatus({ connect_disabled: true, hint: data.hint });
        return;
      }
      if (data.onboarding_url) {
        // Open Stripe's hosted onboarding in a new tab
        window.open(data.onboarding_url, "_blank", "noopener,noreferrer");
        toast.success("Stripe onboarding opened — complete it in the new tab, then return here.", { duration: 8000 });
      }
      loadStripe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe connect failed");
    } finally { setConnecting(false); }
  };

  const payoutToBank = async () => {
    if (!window.confirm(`Send $${F(summary?.unwithdrawn_balance)} to your bank via Stripe? This closes the loop.`)) return;
    setPayingOut(true);
    try {
      const { data } = await api.post("/earnings/stripe/payout");
      if (data.transferred > 0) {
        toast.success(`Stripe transfer created — $${F(data.transferred)}. Transfer ID ${data.transfer_id.slice(0, 12)}…`, { duration: 8000 });
      } else {
        toast.info(data.message || "Nothing to send");
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payout failed"); }
    finally { setPayingOut(false); }
  };

  const s = summary || {};
  return (
    <div className="space-y-6" data-testid="earnings-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Publisher</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-2">
            <DollarSign className="w-7 h-7" /> Earnings
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Signal fees your followers paid on profitable copied trades — accrues in real time, withdraw anytime.
          </p>
        </div>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`earn-days-${d}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      days === d ? "bg-foreground text-background border-foreground" : "border-border text-muted-foreground hover:text-foreground"
                    }`}>{d}d</button>
          ))}
        </div>
      </div>

      {/* KPIs + withdraw */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5 rounded-2xl border-2 border-[hsl(var(--gain))]/40" data-testid="earn-balance-card">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <Wallet className="w-3 h-3" /> Withdrawable
          </div>
          <div className="text-3xl font-mono-tab gain mt-2" data-testid="earn-withdrawable">
            ${F(s.unwithdrawn_balance)}
          </div>
          <Button onClick={withdraw} disabled={withdrawing || !s.unwithdrawn_balance}
                  data-testid="earn-withdraw-btn" className="mt-3 rounded-full w-full">
            {withdrawing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowDownToLine className="w-4 h-4 mr-2" />}
            Withdraw to cash
          </Button>
        </Card>
        <Card className="p-5 rounded-2xl" data-testid="earn-total-card">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Total earned ({days}d)
          </div>
          <div className="text-3xl font-mono-tab mt-2" data-testid="earn-total">${F(s.total_earned)}</div>
          <div className="text-xs text-muted-foreground mt-2">
            Across {s.trades || 0} profitable copied trade{(s.trades || 0) === 1 ? "" : "s"}
          </div>
        </Card>
        <Card className="p-5 rounded-2xl" data-testid="earn-avg-card">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Avg per trade</div>
          <div className="text-3xl font-mono-tab mt-2">
            ${F(s.trades ? s.total_earned / s.trades : 0)}
          </div>
        </Card>
      </div>

      {/* Stripe payout rail */}
      <Card className="p-5 rounded-2xl border-dashed" data-testid="stripe-payout-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
              <Landmark className="w-3.5 h-3.5" /> Bank payout via Stripe
            </div>
            <div className="text-lg font-display font-semibold mt-1">
              {stripeStatus?.connect_disabled ? "Stripe Connect not enabled yet"
                : stripeStatus?.payouts_enabled ? "Payouts enabled — send to bank"
                : stripeStatus?.connected ? "Onboarding in progress"
                : "Connect a payout account"}
            </div>
            <div className="text-sm text-muted-foreground mt-1 max-w-xl">
              {stripeStatus?.connect_disabled
                ? stripeStatus.hint
                : stripeStatus?.payouts_enabled
                  ? "Your Stripe Express account is verified. Withdrawals below will move real money to the bank account you set on Stripe."
                  : stripeStatus?.connected
                    ? "Finish the KYC steps in Stripe's hosted onboarding to unlock bank payouts."
                    : "Aanar integrates with Stripe Connect Express so your signal-fee earnings can settle to your bank instead of just the virtual cash balance."}
            </div>
            {stripeStatus?.requirements?.length > 0 && (
              <div className="mt-2 text-xs text-[hsl(var(--ai))] flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" />
                Stripe still needs: {stripeStatus.requirements.slice(0, 3).join(", ")}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {!stripeStatus?.payouts_enabled && (
              <Button onClick={connectStripe} disabled={connecting}
                      data-testid="stripe-connect-btn"
                      variant="outline"
                      className="rounded-full">
                {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                {stripeStatus?.connected ? "Continue onboarding" : "Connect payout account"}
              </Button>
            )}
            {stripeStatus?.payouts_enabled && (
              <Button onClick={payoutToBank} disabled={payingOut || !s.unwithdrawn_balance}
                      data-testid="stripe-payout-btn"
                      className="rounded-full">
                {payingOut ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Landmark className="w-4 h-4 mr-2" />}
                Send ${F(s.unwithdrawn_balance)} to bank
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Time series */}
      <Card className="p-5 rounded-2xl" data-testid="earn-series-card">
        <div className="text-lg font-display font-semibold mb-2">Daily accrual</div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-8 text-center flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : s.daily?.length ? (
          <>
            <SparkArea series={s.daily} height={140} />
            <div className="mt-2 flex justify-between text-[10px] text-muted-foreground">
              <span>{s.daily[0].date}</span>
              <span>{s.daily.at(-1).date}</span>
            </div>
          </>
        ) : (
          <div className="text-sm text-muted-foreground py-8 text-center" data-testid="earn-series-empty">
            No fees yet in the last {days} days. Publish a bot with a signal fee to start earning when followers profit.
          </div>
        )}
      </Card>

      {/* Top bots */}
      <Card className="p-5 rounded-2xl" data-testid="earn-top-bots-card">
        <div className="text-lg font-display font-semibold mb-3 flex items-center gap-2">
          <Users className="w-4 h-4" /> Top earning bots
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
        ) : (s.top_bots || []).length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="earn-top-empty">
            No earning bots yet — publish one from the Auto-Trade page and set a fee %.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-2 font-medium">Bot</th>
                  <th className="text-left py-2 font-medium">Fee %</th>
                  <th className="text-right py-2 font-medium">Followers</th>
                  <th className="text-right py-2 font-medium">Trades</th>
                  <th className="text-right py-2 font-medium">Earned</th>
                </tr>
              </thead>
              <tbody>
                {s.top_bots.map((b) => (
                  <tr key={b.bot_id} className="border-b border-border/60" data-testid={`earn-top-row-${b.bot_id}`}>
                    <td className="py-2 font-medium">
                      {b.name}
                      {b.public && <Badge variant="secondary" className="rounded-full text-[9px] ml-2">public</Badge>}
                    </td>
                    <td className="py-2 text-muted-foreground">{b.signal_fee_pct != null ? `${b.signal_fee_pct}%` : "—"}</td>
                    <td className="py-2 text-right font-mono-tab">
                      {b.followers}<span className="text-muted-foreground text-xs"> / {b.profitable_followers} profit</span>
                    </td>
                    <td className="py-2 text-right font-mono-tab">{b.trades}</td>
                    <td className="py-2 text-right font-mono-tab gain">${F(b.fees_earned)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="text-[10px] text-center text-muted-foreground" data-testid="earn-privacy-note">
        Followers pay only on realized profit — never on losses. Fees accumulate here and can be withdrawn any time.
      </div>
    </div>
  );
}
